/**
 * selection-store.js — the selection Set, with anchor/range semantics.
 *
 * State only. No DOM, no eventBus, no policy, no imports — pick mode, paint
 * mode, collab guards, shared-vs-page rules and page scoping all live in the
 * controller that owns the single write path into this store. That separation
 * is what makes the model testable in node, which is why it lives in `core/`
 * rather than in `shell.js` (unimportable by a test) or in `StateStore`
 * (selection is not document state and must never reach the collab wire).
 *
 * Vocabulary, kept distinct because today's code conflates them:
 *   anchor  — the fixed end of a Shift range. Moved by a plain select or a
 *             toggle; NEVER by extendTo, which is what lets a range be
 *             recomputed from one anchor instead of accumulating.
 *   lead    — the moving end; the row that owns keyboard focus.
 *   primary — === lead. The single id every single-target consumer acts on.
 *
 * At size 1, anchor === lead === primary, so a single selection is
 * indistinguishable from the scalar it replaces.
 *
 * Ordering: `getIds()` returns document order, never click order. Callers pass
 * an `order` array on writes (the controller derives it from state); the store
 * re-sorts against it so ids added by toggle land in the right slot. Ids absent
 * from `order` keep their relative insertion order at the end, so a write can
 * never silently drop a member.
 */

export class SelectionStore {
  constructor() {
    /** @type {string[]} document-ordered */
    this._ids = [];
    /** @type {string|null} */
    this._anchor = null;
    /** @type {string|null} */
    this._lead = null;
    /** @type {Set<Function>} */
    this._subscribers = new Set();
    /** Reentrancy: writes issued from inside a notify are deferred. */
    this._notifying = false;
    /** @type {Array<Function>} */
    this._queue = [];
  }

  // ── reads ──────────────────────────────────────────────────────────────

  /** @returns {string[]} defensive copy, document order */
  getIds() {
    return this._ids.slice();
  }

  /** @returns {Set<string>} defensive copy */
  getSet() {
    return new Set(this._ids);
  }

  size() {
    return this._ids.length;
  }

  isEmpty() {
    return this._ids.length === 0;
  }

  isMulti() {
    return this._ids.length > 1;
  }

  isSelected(id) {
    return !!id && this._ids.includes(id);
  }

  /** The single id all single-target UI acts on. `null` when empty. */
  getPrimary() {
    return this._lead;
  }

  getAnchor() {
    return this._anchor;
  }

  getLead() {
    return this._lead;
  }

  // ── writes ─────────────────────────────────────────────────────────────
  // Every write returns whether anything actually changed. A write whose
  // resulting (ids, anchor, lead) triple equals the previous one notifies
  // nobody — this subsumes the hand-rolled "alreadySelected" dedupe guards in
  // section-list, glass-overlay and the shell.

  /**
   * Replace the selection with exactly `id`. Anchor and lead both move.
   * This is the plain-click / programmatic path, and the single-select path.
   */
  select(id, opts = {}) {
    if (!id) return this.clear(opts);
    return this._commit([id], id, id, opts);
  }

  /**
   * Replace the selection wholesale.
   * @param {string[]} ids
   * @param {{order?: string[], anchor?: string, lead?: string}} [opts]
   */
  set(ids, opts = {}) {
    const next = this._normalize(ids, opts.order);
    if (next.length === 0) return this.clear(opts);
    const lead = next.includes(opts.lead) ? opts.lead : next[next.length - 1];
    const anchor = next.includes(opts.anchor) ? opts.anchor : lead;
    return this._commit(next, anchor, lead, opts);
  }

  /**
   * Add `id` if absent, remove it if present.
   * Adding moves anchor and lead to `id` (Finder/Explorer behaviour — the
   * anchor follows the toggled row even on removal, so a subsequent Shift
   * range grows from where the user last acted).
   * Removing repairs the lead to the nearest surviving id, preferring the
   * predecessor in document order.
   */
  toggle(id, opts = {}) {
    if (!id) return false;
    if (!this.isSelected(id)) {
      return this._commit(this._normalize([...this._ids, id], opts.order), id, id, opts);
    }
    const removedIndex = this._ids.indexOf(id);
    const next = this._ids.filter((x) => x !== id);
    if (next.length === 0) return this.clear(opts);
    const lead = next[Math.max(0, removedIndex - 1)] ?? next[next.length - 1];
    return this._commit(next, id, lead, opts);
  }

  /**
   * Select the inclusive range from the anchor to `id` over `order`.
   *
   * REPLACES rather than unions, so repeated Shift-clicks from one anchor
   * recompute instead of accumulating — which is also what makes Shift+Arrow
   * shrink back correctly. `additive: true` unions instead (Cmd+Shift+click).
   *
   * The anchor is deliberately left untouched. Degrades to a plain select when
   * there is no anchor, or when either end is missing from `order` (a filtered
   * or freshly-removed row) — never throws, never selects something invisible.
   *
   * @param {string} id
   * @param {{order: string[], additive?: boolean}} opts
   */
  extendTo(id, opts = {}) {
    const order = Array.isArray(opts.order) ? opts.order : [];
    if (!id) return false;
    if (!this._anchor) return this.select(id, opts);
    const a = order.indexOf(this._anchor);
    const b = order.indexOf(id);
    if (a === -1 || b === -1) return this.select(id, opts);
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    const range = order.slice(lo, hi + 1);
    const next = opts.additive ? this._normalize([...this._ids, ...range], order) : range;
    return this._commit(next, this._anchor, id, opts);
  }

  /** Union. Lead moves to the last added id. */
  add(ids, opts = {}) {
    const incoming = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (incoming.length === 0) return false;
    const next = this._normalize([...this._ids, ...incoming], opts.order);
    const lead = incoming[incoming.length - 1];
    return this._commit(next, lead, lead, opts);
  }

  /** Difference. Anchor and lead are repaired if they were removed. */
  remove(ids, opts = {}) {
    const drop = new Set((Array.isArray(ids) ? ids : [ids]).filter(Boolean));
    if (drop.size === 0) return false;
    const next = this._ids.filter((x) => !drop.has(x));
    if (next.length === this._ids.length) return false;
    if (next.length === 0) return this.clear(opts);
    const anchor = next.includes(this._anchor) ? this._anchor : next[next.length - 1];
    const lead = next.includes(this._lead) ? this._lead : anchor;
    return this._commit(next, anchor, lead, opts);
  }

  /** Empty the selection. */
  clear(opts = {}) {
    return this._commit([], null, null, opts);
  }

  /**
   * Drop ids that are no longer valid, repairing anchor and lead.
   * For content churn — a collab peer's delete, an undo, a re-render.
   * @param {Iterable<string>} validIds
   */
  prune(validIds, opts = {}) {
    const valid = validIds instanceof Set ? validIds : new Set(validIds || []);
    const next = this._ids.filter((id) => valid.has(id));
    if (next.length === this._ids.length) return false;
    if (next.length === 0) return this.clear({ ...opts, reason: opts.reason || 'pruned' });
    const anchor = next.includes(this._anchor) ? this._anchor : next[next.length - 1];
    const lead = next.includes(this._lead) ? this._lead : anchor;
    return this._commit(next, anchor, lead, opts);
  }

  /**
   * Hard clear for an identity boundary — active-page change, project or root
   * replacement, tab-guard promotion.
   *
   * Distinct from `prune()` on purpose: prune validates ids against content,
   * and cannot notice that a *different* project happens to reuse an id, nor
   * that a shared selection stayed technically valid across a page switch.
   */
  reset(opts = {}) {
    return this.clear({ ...opts, reason: opts.reason || 'reset' });
  }

  // ── subscription ───────────────────────────────────────────────────────

  /**
   * @param {(change: {ids: string[], primary: string|null, anchor: string|null,
   *   lead: string|null, previousIds: string[], previousPrimary: string|null,
   *   size: number, source?: string, reason?: string, meta?: object}) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    this._subscribers.add(fn);
    return () => this._subscribers.delete(fn);
  }

  // ── internals ──────────────────────────────────────────────────────────

  /** Dedupe, drop falsy, and sort against `order` when one is supplied. */
  _normalize(ids, order) {
    const seen = new Set();
    const out = [];
    for (const id of ids || []) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    if (!Array.isArray(order) || order.length === 0) return out;
    const rank = new Map(order.map((id, i) => [id, i]));
    // Ids absent from `order` sort last, keeping their relative order, so a
    // stale-but-still-selected id is never silently dropped by a sort.
    return out
      .map((id, i) => ({ id, r: rank.has(id) ? rank.get(id) : Number.MAX_SAFE_INTEGER, i }))
      .sort((x, y) => x.r - y.r || x.i - y.i)
      .map((x) => x.id);
  }

  /**
   * The one place state changes. Commits BEFORE notifying, so a subscriber
   * reading getPrimary() during the notify sees the new value — today that
   * depends on eventBus registration order, and section-list races the shell.
   */
  _commit(ids, anchor, lead, opts = {}) {
    if (this._notifying) {
      // A write from inside a notify is deferred until the loop drains, so two
      // notification sets can never interleave.
      this._queue.push(() => this._commit(ids, anchor, lead, opts));
      return false;
    }

    const sameIds = ids.length === this._ids.length && ids.every((id, i) => id === this._ids[i]);
    if (sameIds && anchor === this._anchor && lead === this._lead) return false;

    const previousIds = this._ids;
    const previousPrimary = this._lead;

    this._ids = ids;
    this._anchor = anchor;
    this._lead = lead;

    const change = {
      ids: this._ids.slice(),
      primary: this._lead,
      anchor: this._anchor,
      lead: this._lead,
      previousIds,
      previousPrimary,
      size: this._ids.length,
      source: opts.source,
      reason: opts.reason,
      meta: opts.meta
    };

    this._notifying = true;
    try {
      for (const fn of [...this._subscribers]) {
        try {
          fn(change);
        } catch (err) {
          // One bad subscriber must not abort the rest or leave the store
          // mid-update — the state is already committed above.
          console.error('[selection-store] subscriber threw', err);
        }
      }
    } finally {
      this._notifying = false;
    }

    while (this._queue.length) {
      const next = this._queue.shift();
      next();
    }
    return true;
  }
}

/** The section-selection singleton. Element selection gets its own instance. */
export const selection = new SelectionStore();
